import { useState } from 'react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSaveSearch } from '@/hooks/use-saved-searches'
import type { SavedSearchScope } from '@/lib/saved-searches'

/** Names a filter and saves it — personal (mine) or shared (org). Visible to
 *  everyone either way; the scope is ownership, not privacy. */
export function SaveSearchDialog({
  open,
  onOpenChange,
  filter,
  defaultName = '',
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  filter: string
  defaultName?: string
}) {
  const [name, setName] = useState(defaultName)
  const [scope, setScope] = useState<SavedSearchScope>('user')
  const save = useSaveSearch()

  function submit() {
    const trimmed = name.trim()
    if (!trimmed) return
    save.mutate(
      { name: trimmed, filter, scope },
      {
        onSuccess: () => {
          toast.success(`Saved "${trimmed}"`)
          onOpenChange(false)
          setName('')
        },
        onError: (err) =>
          toast.error('Could not save search', {
            description: err instanceof Error ? err.message : undefined,
          }),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Save search</DialogTitle>
          <DialogDescription className="font-mono text-[11px]">{filter || 'no filter'}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-1">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="saved-search-name">Name</Label>
            <Input
              id="saved-search-name"
              value={name}
              autoFocus
              placeholder="e.g. AFH UK · site"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !save.isPending && submit()}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Where</Label>
            <div className="border-border bg-surface flex items-center gap-px rounded-lg border p-0.5">
              {(
                [
                  ['user', 'Mine'],
                  ['org', 'Shared (org)'],
                ] as const
              ).map(([value, label]) => (
                <Button
                  key={value}
                  type="button"
                  size="sm"
                  variant={scope === value ? 'secondary' : 'ghost'}
                  className="h-7 flex-1 text-xs"
                  onClick={() => setScope(value)}
                >
                  {label}
                </Button>
              ))}
            </div>
            <p className="text-faint-foreground text-[11px]">
              Everyone can see both — this just records who owns it.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name.trim() || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
